// src/pages/UploadDocuments.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ProgressTracker from "../components/ProgressTracker";

const API_WRK = process.env.REACT_APP_API_WRK;

const idTypes = [
  "Passport",
  "Driver's License",
  "National ID",
  "PR Card",
  "Citizenship Card",
  "Service ID",
];

const UploadDocuments = () => {
  const navigate = useNavigate();
  const workerId = localStorage.getItem("workerId");

  const [step, setStep] = useState(1);
  const [citizenStatus, setCitizenStatus] = useState("");
  const [id1Type, setId1Type] = useState("");
  const [id1Front, setId1Front] = useState(null);
  const [id1Back, setId1Back] = useState(null);
  const [id2Type, setId2Type] = useState("");
  const [id2Front, setId2Front] = useState(null);
  const [id2Back, setId2Back] = useState(null);
  const [permitFile, setPermitFile] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!workerId) navigate("/worker-login");
  }, [workerId, navigate]);

  const nextStep = () => setStep((prev) => prev + 1);
  const prevStep = () => setStep((prev) => prev - 1);

  const handleSubmit = async () => {
    if (
      !citizenStatus ||
      !id1Type ||
      !id2Type ||
      !id1Front ||
      !id1Back ||
      !id2Front ||
      !id2Back ||
      !selfieFile
    ) {
      setError("Please upload all required documents.");
      return;
    }

    if (id1Type === id2Type) {
      setError("ID types must be different.");
      return;
    }

    if (citizenStatus === "no" && !permitFile) {
      setError("Work/Study Permit required for international workers.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("workerId", workerId);
      formData.append("isInternational", citizenStatus === "no");
      formData.append("id1Type", id1Type);
      formData.append("id1Front", id1Front);
      formData.append("id1Back", id1Back);
      formData.append("id2Type", id2Type);
      formData.append("id2Front", id2Front);
      formData.append("id2Back", id2Back);
      if (permitFile) formData.append("permitFile", permitFile);
      formData.append("selfieFile", selfieFile);

      await axios.post(`${API_WRK}/upload-ids`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      navigate("/select-services");
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || "Upload failed. Please try again.");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-indigo-50 px-4">
      <div className="bg-white rounded-2xl shadow-xl p-6 max-w-xl w-full space-y-6">
        <ProgressTracker currentStep={3} />
        <h2 className="text-xl font-bold text-indigo-700 text-center">Upload Documents</h2>

        {/* Step 1: Citizenship */}
        {step === 1 && (
          <div>
            <h3 className="font-semibold text-indigo-600 mb-2">Are you a Citizen or PR?</h3>
            <div className="flex gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  value="yes"
                  checked={citizenStatus === "yes"}
                  onChange={() => setCitizenStatus("yes")}
                />
                Yes
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  value="no"
                  checked={citizenStatus === "no"}
                  onChange={() => setCitizenStatus("no")}
                />
                No
              </label>
            </div>
          </div>
        )}

        {/* Step 2: First ID */}
        {step === 2 && (
          <div>
            <h3 className="font-semibold text-indigo-600 mb-2">Upload First ID (Front & Back)</h3>
            <select
              value={id1Type}
              onChange={(e) => setId1Type(e.target.value)}
              className="w-full border px-3 py-2 rounded-lg"
            >
              <option value="">Select ID Type</option>
              {idTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>

            <div className="mt-4 flex flex-col gap-2">
              <label className="cursor-pointer bg-indigo-600 text-white px-4 py-2 rounded text-center hover:bg-indigo-700">
                📤 Upload Front
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setId1Front(e.target.files[0])}
                  className="hidden"
                />
              </label>
              {id1Front && <p className="text-green-600 text-sm">✅ {id1Front.name}</p>}

              <label className="cursor-pointer bg-indigo-600 text-white px-4 py-2 rounded text-center hover:bg-indigo-700">
                📤 Upload Back
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setId1Back(e.target.files[0])}
                  className="hidden"
                />
              </label>
              {id1Back && <p className="text-green-600 text-sm">✅ {id1Back.name}</p>}
            </div>
          </div>
        )}

        {/* Step 3: Second ID */}
        {step === 3 && (
          <div>
            <h3 className="font-semibold text-indigo-600 mb-2">Upload Second ID (Front & Back)</h3>
            <select
              value={id2Type}
              onChange={(e) => setId2Type(e.target.value)}
              className="w-full border px-3 py-2 rounded-lg"
            >
              <option value="">Select ID Type</option>
              {idTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>

            <div className="mt-4 flex flex-col gap-2">
              <label className="cursor-pointer bg-indigo-600 text-white px-4 py-2 rounded text-center hover:bg-indigo-700">
                📤 Upload Front
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setId2Front(e.target.files[0])}
                  className="hidden"
                />
              </label>
              {id2Front && <p className="text-green-600 text-sm">✅ {id2Front.name}</p>}

              <label className="cursor-pointer bg-indigo-600 text-white px-4 py-2 rounded text-center hover:bg-indigo-700">
                📤 Upload Back
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setId2Back(e.target.files[0])}
                  className="hidden"
                />
              </label>
              {id2Back && <p className="text-green-600 text-sm">✅ {id2Back.name}</p>}
            </div>
          </div>
        )}

        {/* Step 4: Permit if needed */}
        {step === 4 && citizenStatus === "no" && (
          <div>
            <h3 className="font-semibold text-indigo-600 mb-2">Upload Work/Study Permit</h3>
            <label className="cursor-pointer bg-indigo-600 text-white px-4 py-2 rounded text-center hover:bg-indigo-700">
              📤 Upload Permit
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setPermitFile(e.target.files[0])}
                className="hidden"
              />
            </label>
            {permitFile && <p className="text-green-600 text-sm mt-1">✅ {permitFile.name}</p>}
          </div>
        )}

        {/* Step 5: Selfie */}
        {(step === 4 && citizenStatus === "yes") || step === 5 ? (
          <div>
            <h3 className="font-semibold text-indigo-600 mb-2">Upload Selfie with ID</h3>
            <p className="text-sm text-gray-600 mb-2">Make sure your face and ID are clearly visible.</p>
            <label className="cursor-pointer bg-indigo-600 text-white px-4 py-2 rounded text-center hover:bg-indigo-700">
              📷 Upload Selfie
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setSelfieFile(e.target.files[0])}
                className="hidden"
              />
            </label>
            {selfieFile && <p className="text-green-600 text-sm mt-1">✅ {selfieFile.name}</p>}
          </div>
        ) : null}

        {/* Error Message */}
        {error && <p className="text-red-500 text-sm text-center">{error}</p>}

        {/* Navigation */}
        <div className="flex justify-between pt-2">
          {step > 1 && (
            <button
              className="px-4 py-2 rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
              onClick={prevStep}
            >
              Back
            </button>
          )}
          {((step === 4 && citizenStatus === "yes") || step === 5) ? (
            <button
              className="ml-auto px-4 py-2 rounded bg-indigo-600 text-white hover:bg-indigo-700"
              onClick={handleSubmit}
            >
              Submit
            </button>
          ) : (
            <button
              className="ml-auto px-4 py-2 rounded bg-indigo-600 text-white hover:bg-indigo-700"
              onClick={nextStep}
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadDocuments;
